import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { db } from "../../Firebase";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import emailjs from "@emailjs/browser";
import "../../Admin.css";
import AdminNav from "../../components/AdminNav";

const TZ = "America/Chicago";

// ---------- helpers ----------

function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isoToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function randomBlobId(len = 20) {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function linesToArray(text) {
  return String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function arrayToLines(arr) {
  return (Array.isArray(arr) ? arr : []).join("\n");
}

function newItem(id) {
  return {
    itemId: id,
    category: "Repairs",
    name: "",
    description: "",
    qty: 1,
    unit: "ea",
    unitPrice: 0,
    taxable: true,
    references: [],
    sortOrder: 1,
    photos: [],
    costBreakdown: {
      materials: [],
      labor: { hours: 0, rate: 0 },
      equipment: [],
      subcontract: [],
    },
  };
}

function newGroup(id) {
  return { groupId: id, title: "", notes: "", items: [newItem(`${id}-001`)] };
}

function computeOptionTotals(option, pricing) {
  const taxRate = safeNum(pricing?.taxRate);
  const taxMode = pricing?.taxMode || "exclusive";
  const discounts = Array.isArray(pricing?.discounts) ? pricing.discounts : [];
  const fees = Array.isArray(pricing?.fees) ? pricing.fees : [];

  let subtotal = 0;
  let taxableSubtotal = 0;

  for (const g of option.areas || []) {
    for (const it of g.items || []) {
      const qty = safeNum(it.qty);
      const unitPrice = safeNum(it.unitPrice);
      const line = qty * unitPrice;
      subtotal += line;
      if (it.taxable) taxableSubtotal += line;
    }
  }

  // Fees
  let feesTotal = 0;
  let taxableFees = 0;
  for (const f of fees) {
    const amt = safeNum(f.amount);
    feesTotal += amt;
    if (f.taxable) taxableFees += amt;
  }

  // Discounts (on subtotal)
  let discountTotal = 0;
  for (const d of discounts) {
    const type = d.type || "percent";
    const value = safeNum(d.value);
    if (type === "percent") discountTotal += subtotal * value;
    else discountTotal += value;
  }
  discountTotal = Math.max(0, Math.min(discountTotal, subtotal));

  const taxableRatio = subtotal > 0 ? taxableSubtotal / subtotal : 0;
  const taxableDiscountShare = discountTotal * taxableRatio;

  const taxableAfterDiscount = Math.max(0, taxableSubtotal - taxableDiscountShare);
  const subtotalAfterDiscount = Math.max(0, subtotal - discountTotal);

  let taxTotal = 0;
  if (taxMode === "exclusive") {
    taxTotal = (taxableAfterDiscount + taxableFees) * taxRate;
  } else {
    taxTotal = 0;
  }

  const grandTotal = subtotalAfterDiscount + feesTotal + taxTotal;

  return {
    subtotal,
    taxableSubtotal,
    taxTotal,
    discountTotal,
    feesTotal,
    grandTotal,
  };
}

function statusClass(status) {
  const s = String(status || "draft").toLowerCase();
  if (s === "accepted") return "pill ok";
  if (s === "sent" || s === "viewed" || s === "ready") return "pill info";
  if (s === "declined" || s === "void") return "pill bad";
  if (s === "expired") return "pill warn";
  return "pill";
}

// ---------- component ----------
export default function AdminEstimateEdit() {
  const nav = useNavigate();
  const { blobId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // preserve createdAt
  const [createdAtPersist, setCreatedAtPersist] = useState(null);

  // Local editable model (includes "Text" fields for multiline UI)
  const [form, setForm] = useState(null);

  // ---- EmailJS ----
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  const estimateRef = useMemo(() => doc(db, "publicEstimates", blobId), [blobId]);

  useEffect(() => {
    document.title = "Edit Estimate | Admin";
  }, []);

  // -------- load doc --------
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErrorMsg("");
      setStatusMsg("");

      try {
        const snap = await getDoc(estimateRef);
        if (!snap.exists()) throw new Error(`Estimate not found: publicEstimates/${blobId}`);

        const data = snap.data();
        if (!alive) return;

        setCreatedAtPersist(data.createdAt || null);

        // Normalize into UI-friendly editable shape
        const normalized = {
          status: data.status || "draft",
          estimateNumber: data.estimateNumber || "",
          revision: safeNum(data.revision) || 1,
          revisionOf: data.revisionOf || null,

          validUntil: data.validUntil || "",
          currency: data.currency || "USD",
          language: data.language || "en-US",
          timezone: data.timezone || TZ,

          tagsText: arrayToLines(data.tags || []),

          customer: {
            type: data.customer?.type || "company",
            companyName: data.customer?.companyName || "",
            name: data.customer?.name || "",
            phone: data.customer?.phone || "",
            email: data.customer?.email || "",
            billingAddress: {
              line1: data.customer?.billingAddress?.line1 || "",
              line2: data.customer?.billingAddress?.line2 || "",
              city: data.customer?.billingAddress?.city || "",
              state: data.customer?.billingAddress?.state || "TX",
              postal: data.customer?.billingAddress?.postal || "",
              country: data.customer?.billingAddress?.country || "US",
            },
            contacts: Array.isArray(data.customer?.contacts) ? data.customer.contacts : [],
          },

          site: {
            propertyName: data.site?.propertyName || "",
            siteAddress: {
              line1: data.site?.siteAddress?.line1 || "",
              line2: data.site?.siteAddress?.line2 || "",
              city: data.site?.siteAddress?.city || "",
              state: data.site?.siteAddress?.state || "TX",
              postal: data.site?.siteAddress?.postal || "",
              country: data.site?.siteAddress?.country || "US",
            },
            access: data.site?.access || "",
            siteConstraintsText: arrayToLines(data.site?.siteConstraints || []),
            safetyRequirementsText: arrayToLines(data.site?.safetyRequirements || []),
            roofSystemsText: arrayToLines(data.site?.roofSystems || []),
            approxArea: data.site?.approxArea || "",
            notes: data.site?.notes || "",
          },

          issuer: {
            companyName: data.issuer?.companyName || "Crowned Roofing",
            address: data.issuer?.address || {
              line1: "Houston, TX",
              line2: "",
              city: "Houston",
              state: "TX",
              postal: "",
              country: "US",
            },
            phone: data.issuer?.phone || "",
            email: data.issuer?.email || "estimates@crownedroofing.com",
            website: data.issuer?.website || "crownedroofing.com",
            license: data.issuer?.license || "",
            preparedBy: data.issuer?.preparedBy || { name: "", title: "" },
          },

          pricing: {
            taxMode: data.pricing?.taxMode || "exclusive",
            taxRate: safeNum(data.pricing?.taxRate) || 0,
            discounts:
              Array.isArray(data.pricing?.discounts) && data.pricing.discounts.length
                ? data.pricing.discounts
                : [{ label: "Discount", type: "percent", value: 0 }],
            fees:
              Array.isArray(data.pricing?.fees) && data.pricing.fees.length
                ? data.pricing.fees
                : [{ label: "Mobilization", amount: 0, taxable: false }],
            rounding: data.pricing?.rounding || "nearest_cent",
          },

          options:
            Array.isArray(data.options) && data.options.length
              ? data.options.map((o) => ({
                  optionId: o.optionId || "A",
                  title: o.title || `Option ${o.optionId || "A"}`,
                  summary: o.summary || "",
                  includedText: arrayToLines(o.included || []),
                  excludedText: arrayToLines(o.excluded || []),
                  areas:
                    Array.isArray(o.areas) && o.areas.length
                      ? o.areas.map((g) => ({
                          groupId: g.groupId || `G-${o.optionId}-01`,
                          title: g.title || "",
                          notes: g.notes || "",
                          items:
                            Array.isArray(g.items) && g.items.length
                              ? g.items.map((it) => ({
                                  itemId: it.itemId || `${g.groupId}-001`,
                                  category: it.category || "Repairs",
                                  name: it.name || "",
                                  description: it.description || "",
                                  qty: safeNum(it.qty) || 0,
                                  unit: it.unit || "ea",
                                  unitPrice: safeNum(it.unitPrice) || 0,
                                  taxable: it.taxable !== false,
                                  references: Array.isArray(it.references) ? it.references : [],
                                  sortOrder: safeNum(it.sortOrder) || 1,
                                  photos: Array.isArray(it.photos) ? it.photos : [],
                                  costBreakdown: it.costBreakdown || {
                                    materials: [],
                                    labor: { hours: 0, rate: 0 },
                                    equipment: [],
                                    subcontract: [],
                                  },
                                }))
                              : [newItem(`${g.groupId || `G-${o.optionId}-01`}-001`)],
                        }))
                      : [newGroup(`G-${o.optionId || "A"}-01`)],
                  totals: o.totals || {
                    subtotal: 0,
                    taxableSubtotal: 0,
                    taxTotal: 0,
                    discountTotal: 0,
                    feesTotal: 0,
                    grandTotal: 0,
                  },
                }))
              : [
                  {
                    optionId: "A",
                    title: "Option A",
                    summary: "",
                    includedText: "",
                    excludedText: "",
                    areas: [newGroup("G-A-01")],
                    totals: {
                      subtotal: 0,
                      taxableSubtotal: 0,
                      taxTotal: 0,
                      discountTotal: 0,
                      feesTotal: 0,
                      grandTotal: 0,
                    },
                  },
                ],

          schedule: {
            leadTime: data.schedule?.leadTime || "",
            duration: data.schedule?.duration || "",
            workWindow: data.schedule?.workWindow || "",
            milestonesText: arrayToLines(
              (data.schedule?.milestones || []).map((m) => {
                const label = m?.label || "";
                const note = m?.notes || "";
                return note ? `${label} — ${note}` : label;
              })
            ),
          },

          payment: {
            depositRequired: !!data.payment?.depositRequired,
            depositType: data.payment?.depositType || "percent",
            depositValue: safeNum(data.payment?.depositValue) || 0,
            paymentMethodsText: arrayToLines(data.payment?.paymentMethods || []),
            invoicing: data.payment?.invoicing || "",
            lateFeePolicy: data.payment?.lateFeePolicy || "",
          },

          warranty: {
            type: data.warranty?.type || "",
            term: data.warranty?.term || "",
            detailsText: arrayToLines(data.warranty?.details || []),
          },

          assumptionsText: arrayToLines(data.assumptions || []),
          exclusionsText: arrayToLines(data.exclusions || []),
          clarificationsText: arrayToLines(data.clarifications || []),
          termsText: arrayToLines(data.terms || []),

          attachments: Array.isArray(data.attachments) ? data.attachments : [],
          publicAccess: data.publicAccess || { enabled: true, requireEmailGate: false, pin: "" },

          acceptance: data.acceptance || {
            selectedOptionId: "",
            acceptedByName: "",
            acceptedByEmail: "",
            acceptedByTitle: "",
            signature: { path: "", signedAt: "" },
            ipAddress: "",
            userAgent: "",
          },

          audit: Array.isArray(data.audit) ? data.audit : [],
        };

        setForm(normalized);
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load estimate.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [blobId, estimateRef]);

  // default email from form when available
  useEffect(() => {
    if (form?.customer?.email) setEmailTo(form.customer.email);
  }, [form?.customer?.email]);

  // Live totals for UI display
  const optionsWithTotals = useMemo(() => {
    if (!form) return [];
    return (form.options || []).map((opt) => ({
      ...opt,
      totals: computeOptionTotals(opt, form.pricing),
    }));
  }, [form]);

  function setField(path, value) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }

  function updateOption(idx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[idx] = { ...next.options[idx], ...patch };
      return next;
    });
  }

  function updateGroup(optionIdx, groupIdx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[optionIdx].areas[groupIdx] = {
        ...next.options[optionIdx].areas[groupIdx],
        ...patch,
      };
      return next;
    });
  }

  function updateItem(optionIdx, groupIdx, itemIdx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const it = next.options[optionIdx].areas[groupIdx].items[itemIdx];
      next.options[optionIdx].areas[groupIdx].items[itemIdx] = { ...it, ...patch };
      return next;
    });
  }

  function addOption() {
    setForm((prev) => {
      const next = structuredClone(prev);
      const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
      const used = new Set((next.options || []).map((o) => o.optionId));
      const nextId = letters.find((c) => !used.has(c)) || `ALT${(next.options?.length || 0) + 1}`;
      next.options.push({
        optionId: nextId,
        title: `Option ${nextId}`,
        summary: "",
        includedText: "",
        excludedText: "",
        areas: [newGroup(`G-${nextId}-01`)],
        totals: { subtotal: 0, taxableSubtotal: 0, taxTotal: 0, discountTotal: 0, feesTotal: 0, grandTotal: 0 },
      });
      return next;
    });
  }

  function removeOption(idx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options.splice(idx, 1);
      return next;
    });
  }

  function addGroup(optionIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const opt = next.options[optionIdx];
      const n = (opt.areas?.length || 0) + 1;
      opt.areas.push(newGroup(`G-${opt.optionId}-${String(n).padStart(2, "0")}`));
      return next;
    });
  }

  function removeGroup(optionIdx, groupIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[optionIdx].areas.splice(groupIdx, 1);
      return next;
    });
  }

  function addItem(optionIdx, groupIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const group = next.options[optionIdx].areas[groupIdx];
      const n = (group.items?.length || 0) + 1;
      group.items.push(newItem(`${group.groupId}-${String(n).padStart(3, "0")}`));
      return next;
    });
  }

  function removeItem(optionIdx, groupIdx, itemIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[optionIdx].areas[groupIdx].items.splice(itemIdx, 1);
      return next;
    });
  }

  function toFirestorePayload(nextStatus) {
    const payload = structuredClone(form);

    payload.status = nextStatus || payload.status || "draft";

    // multiline -> arrays
    payload.tags = linesToArray(payload.tagsText);
    delete payload.tagsText;

    payload.site.siteConstraints = linesToArray(payload.site.siteConstraintsText);
    payload.site.safetyRequirements = linesToArray(payload.site.safetyRequirementsText);
    payload.site.roofSystems = linesToArray(payload.site.roofSystemsText);
    delete payload.site.siteConstraintsText;
    delete payload.site.safetyRequirementsText;
    delete payload.site.roofSystemsText;

    payload.schedule.milestones = linesToArray(payload.schedule.milestonesText).map((line) => {
      const parts = line.split("—");
      return {
        label: (parts[0] || "").trim(),
        dateTarget: "",
        notes: (parts[1] || "").trim(),
      };
    });
    delete payload.schedule.milestonesText;

    payload.payment.paymentMethods = linesToArray(payload.payment.paymentMethodsText);
    delete payload.payment.paymentMethodsText;

    payload.warranty.details = linesToArray(payload.warranty.detailsText);
    delete payload.warranty.detailsText;

    payload.assumptions = linesToArray(payload.assumptionsText);
    payload.exclusions = linesToArray(payload.exclusionsText);
    payload.clarifications = linesToArray(payload.clarificationsText);
    payload.terms = linesToArray(payload.termsText);
    delete payload.assumptionsText;
    delete payload.exclusionsText;
    delete payload.clarificationsText;
    delete payload.termsText;

    // Options normalize + totals
    payload.options = optionsWithTotals.map((o) => ({
      optionId: o.optionId,
      title: o.title,
      summary: o.summary,
      included: linesToArray(o.includedText),
      excluded: linesToArray(o.excludedText),
      areas: (o.areas || []).map((g) => ({
        groupId: g.groupId,
        title: g.title,
        notes: g.notes,
        items: (g.items || []).map((it) => ({
          itemId: it.itemId,
          category: it.category,
          name: it.name,
          description: it.description,
          qty: safeNum(it.qty),
          unit: it.unit,
          unitPrice: safeNum(it.unitPrice),
          taxable: !!it.taxable,
          references: Array.isArray(it.references) ? it.references : [],
          sortOrder: safeNum(it.sortOrder),
          photos: Array.isArray(it.photos) ? it.photos : [],
          costBreakdown: it.costBreakdown || {
            materials: [],
            labor: { hours: 0, rate: 0 },
            equipment: [],
            subcontract: [],
          },
        })),
      })),
      totals: o.totals,
    }));

    // timestamps
    payload.updatedAt = serverTimestamp();

    // Preserve createdAt
    if (createdAtPersist) payload.createdAt = createdAtPersist;
    else payload.createdAt = serverTimestamp();

    // status-driven timestamps
    if (payload.status === "sent" && !payload.sentAt) payload.sentAt = serverTimestamp();
    if (payload.status === "accepted" && !payload.acceptedAt) payload.acceptedAt = serverTimestamp();
    if (payload.status === "declined" && !payload.declinedAt) payload.declinedAt = serverTimestamp();
    if (payload.status === "void" && !payload.voidedAt) payload.voidedAt = serverTimestamp();

    return payload;
  }

  // ---------- EmailJS helpers + sender ----------
  function emailIsValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
  }

  function formatDateShort(input) {
    if (!input) return "";
    try {
      let d = null;
      if (input instanceof Date) d = input;
      else if (typeof input === "string" || typeof input === "number") d = new Date(input);
      else if (input?.toDate) d = input.toDate();
      else if (typeof input?.seconds === "number") d = new Date(input.seconds * 1000);
      if (!d || Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    } catch {
      return "";
    }
  }

  function moneyUSD(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  }

  function pickGrandTotalFromUIOrDoc(docData) {
    // Prefer doc acceptance selection if present; else option A/first
    const selectedOptionId =
      docData?.acceptance?.selectedOptionId ||
      form?.acceptance?.selectedOptionId ||
      docData?.options?.[0]?.optionId ||
      form?.options?.[0]?.optionId ||
      "A";

    const idx =
      (form?.options || []).findIndex((o) => o.optionId === selectedOptionId);

    const totals =
      idx >= 0
        ? optionsWithTotals?.[idx]?.totals
        : optionsWithTotals?.[0]?.totals;

    return totals?.grandTotal ?? 0;
  }

  async function sendEstimateEmail() {
    setEmailMsg("");

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_ESTIMATE_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setEmailMsg("Missing EmailJS config (.env).");
      return;
    }

    const to_email = String(emailTo || "").trim();
    if (!emailIsValid(to_email)) {
      setEmailMsg("Enter a valid recipient email.");
      return;
    }

    try {
      setEmailSending(true);

      // Source of truth: Firestore doc
      const snap = await getDoc(estimateRef);
      if (!snap.exists()) throw new Error("Estimate not found in Firestore.");

      const data = snap.data();

      // Firestore paths you specified:
      // creation date = /audit/at
      const auditArr = Array.isArray(data?.audit) ? data.audit : [];
      const auditAt = auditArr.find((x) => x?.at)?.at || auditArr[0]?.at || data?.createdAt || null;

      // address: prefer site, fallback to billing
      const project_address =
        data?.site?.siteAddress?.line1 ||
        data?.customer?.billingAddress?.line1 ||
        "";

      // company name = /customer/companyName
      const company_name = data?.customer?.companyName || "";

      // contact name = /customer/name
      const contact_name = data?.customer?.name || "";

      // contact email = /customer/email (not required for send, but useful fallback)
      const contact_email = data?.customer?.email || "";

      // estimate number = /estimateNumber
      const estimate_number = String(data?.estimateNumber || form?.estimateNumber || blobId || "");

      const client_name = contact_name || company_name || "Client";

      const estimate_date = formatDateShort(auditAt) || formatDateShort(new Date());

      // grand total: you didn’t provide a Firestore path for this yet,
      // so we use your computed UI totals (based on selected option)

      const estimate_url = `${window.location.origin}/estimate/${blobId}`;

      const templateParams = {
        to_email,
        client_name,
        estimate_number,
        estimate_date,
        project_address,
        estimate_url,
      };

      await emailjs.send(serviceId, templateId, templateParams, { publicKey });

      // If you want: auto-fill email from contact_email if input was blank
      // (we won't overwrite user's value if they typed it)
      if (!emailTo && contact_email) setEmailTo(contact_email);

      setEmailMsg(`Sent to ${to_email}`);
    } catch (e) {
      setEmailMsg(e?.message || e?.text || "Send failed.");
    } finally {
      setEmailSending(false);
    }
  }

  async function convertToInvoice() {
    if (!form) return;

    // Choose which option becomes the invoice scope:
    // 1) accepted option if present
    // 2) otherwise Option A / first option
    const accepted = form.acceptance?.selectedOptionId;
    const optionId = accepted || form.options?.[0]?.optionId || "A";
    const opt = (form.options || []).find((o) => o.optionId === optionId) || form.options?.[0];

    if (!opt) {
      setErrorMsg("No options found on this estimate. Add at least one option before converting.");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    setStatusMsg("");

    try {
      // Make sure we have the latest estimate snapshot from Firestore (optional but safer)
      const estSnap = await getDoc(estimateRef);
      if (!estSnap.exists()) throw new Error("Estimate doc not found when converting.");

      const est = estSnap.data();

      // Generate invoice doc id
      const invoiceBlobId = randomBlobId(22);

      // Totals: prefer stored option totals, else compute from current form pricing
      const computedTotals = opt.totals?.grandTotal != null
        ? {
            subtotal: opt.totals.subtotal || 0,
            discountTotal: opt.totals.discountTotal || 0,
            feesTotal: opt.totals.feesTotal || 0,
            taxTotal: opt.totals.taxTotal || 0,
            total: opt.totals.grandTotal || 0,
            paymentsTotal: 0,
            balanceDue: opt.totals.grandTotal || 0,
          }
        : (() => {
            const t = computeOptionTotals(opt, form.pricing);
            return {
              subtotal: t.subtotal,
              discountTotal: t.discountTotal,
              feesTotal: t.feesTotal,
              taxTotal: t.taxTotal,
              total: t.grandTotal,
              paymentsTotal: 0,
              balanceDue: t.grandTotal,
            };
          })();

      // Convert option "areas" -> invoice "groups"
      const groups = (opt.areas || []).map((g) => ({
        groupId: g.groupId,
        title: g.title,
        notes: g.notes || "",
        items: (g.items || []).map((it) => ({
          itemId: it.itemId,
          category: it.category || "Work",
          name: it.name || it.itemId,
          description: it.description || "",
          qty: safeNum(it.qty),
          unit: it.unit || "ea",
          unitPrice: safeNum(it.unitPrice),
          taxable: it.taxable !== false,
          photos: Array.isArray(it.photos) ? it.photos : [],
          references: Array.isArray(it.references) ? it.references : [],
          sortOrder: safeNum(it.sortOrder) || 1,
        })),
      }));

      // Invoice number strategy:
      // If you want “CR-INV-YYYY-####” you’ll need a counter doc.
      // For now: derive from estimate number + option
      const baseNum = form.estimateNumber || blobId;
      const invoiceNumber = `INV-${baseNum}-${optionId}`;

      const invoicePayload = {
        // identity
        invoiceNumber,
        status: "open",
        currency: form.currency || "USD",

        issueDate: isoToday(),
        dueDate: addDaysISO(14),

        // issuer/customer/site snapshot
        issuer: est.issuer || form.issuer,
        customer: est.customer || form.customer,
        site: est.site || form.site,

        // link back to estimate
        source: {
          estimateId: blobId,
          selectedOptionId: optionId,
        },

        // snapshot pricing + scope
        pricing: est.pricing || form.pricing,
        groups,

        // money
        totals: computedTotals,

        // payments
        payments: [],

        // content
        notes:
          "Thank you for your business. Please reference the invoice number on your payment.",
        payment: est.payment || form.payment || {},
        warranty: est.warranty || form.warranty || {},

        // optional attachments (start empty or carry forward if you want)
        attachments: [],

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "publicInvoices", invoiceBlobId), invoicePayload);

      setStatusMsg(`Invoice created: ${invoiceNumber}`);

      // Redirect to public invoice view (swap to admin invoice edit later)
      nav(`/invoice/${invoiceBlobId}`);
    } catch (e) {
      setErrorMsg(e?.message || "Failed to convert estimate into invoice.");
    } finally {
      setSaving(false);
    }
  }

  async function save(nextStatus) {
    if (!form) return;
    setSaving(true);
    setErrorMsg("");
    setStatusMsg("");

    try {
      const payload = toFirestorePayload(nextStatus);
      await setDoc(estimateRef, payload, { merge: true });

      setForm((prev) => ({ ...prev, status: payload.status }));
      setStatusMsg("Saved.");
      window.setTimeout(() => setStatusMsg(""), 1400);
    } catch (e) {
      setErrorMsg(e?.message || "Failed to save estimate.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="aePage">
      <AdminNav />
        <div
          className="aeError"
          style={{
            background: "#f8fafc",
            color: "#334155",
            borderColor: "rgba(15,23,42,.10)",
          }}
        >
          Loading estimate…
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="aePage">
      <AdminNav />
        <div className="aeError">{errorMsg || "Estimate not available."}</div>
        <button className="btn btnGhost" onClick={() => nav(-1)}>← Back</button>
      </div>
    );
  }

  return (
    <div className="aePage">
      <AdminNav />
      <div className="aeTop">
        <div>
          <h1>Edit Estimate</h1>
          <p className="aeSub">
            Blob ID: <span className="mono">{blobId}</span>{" "}
            <span className={statusClass(form.status)} style={{ marginLeft: 10 }}>
              {form.status}
            </span>
          </p>
        </div>

        <div className="aeActions">
          <Link className="btn btnGhost" to="/admin/estimates">← Back</Link>
          <button className="btn" disabled={saving} onClick={() => save("draft")}>Save Draft</button>
          <button className="btn" disabled={saving} onClick={() => save("sent")}>Mark Sent</button>
          <button className="btn btnPrimary" disabled={saving} onClick={() => save()}>Save</button>
          <button
            className="btn btnGhost"
            disabled={saving}
            onClick={convertToInvoice}
            type="button"
          >
            Convert → Invoice
          </button>
          <a className="btn btnGhost" href={`/estimate/${blobId}`} target="_blank" rel="noreferrer">
            Public View
          </a>
        </div>
      </div>

      {statusMsg ? (
        <div
          className="aeError"
          style={{
            background: "#ecfeff",
            color: "#155e75",
            borderColor: "rgba(6,182,212,.25)",
          }}
        >
          {statusMsg}
        </div>
      ) : null}
      {errorMsg ? <div className="aeError">{errorMsg}</div> : null}

      {/* EMAIL ESTIMATE */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <h3 style={{ margin: 0 }}>Email Estimate</h3>
          <div className="aeSmall" style={{ color: "#64748b" }}>
            Sends the public estimate link via EmailJS.
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, marginTop: 10 }}>
          <label>
            <div className="aeSmall">To Email</div>
            <input
              className="aeSearch"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="client@email.com"
              disabled={emailSending}
            />
          </label>

          <button
            className="btn btnPrimary"
            type="button"
            onClick={sendEstimateEmail}
            disabled={emailSending || !emailIsValid(emailTo)}
            style={{ alignSelf: "end" }}
          >
            {emailSending ? "Sending…" : "Send"}
          </button>
        </div>

        <div className="aeSmall" style={{ marginTop: 10, color: "#64748b" }}>
          Link:{" "}
          <a href={`/estimate/${blobId}`} target="_blank" rel="noreferrer">
            {window.location.origin}/estimate/{blobId}
          </a>
        </div>

        {emailMsg ? (
          <div
            className="aeError"
            style={{
              marginTop: 10,
              background: String(emailMsg).startsWith("Sent") ? "#ecfeff" : "#fff1f2",
              color: String(emailMsg).startsWith("Sent") ? "#155e75" : "#9f1239",
              borderColor: String(emailMsg).startsWith("Sent")
                ? "rgba(6,182,212,.25)"
                : "rgba(244,63,94,.25)",
            }}
          >
            {emailMsg}
          </div>
        ) : null}
      </div>

      {/* STATUS + IDENTITY */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Identity</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Status</div>
            <select
              className="aeSearch"
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="draft">draft</option>
              <option value="ready">ready</option>
              <option value="sent">sent</option>
              <option value="viewed">viewed</option>
              <option value="accepted">accepted</option>
              <option value="declined">declined</option>
              <option value="expired">expired</option>
              <option value="void">void</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Estimate Number</div>
            <input
              className="aeSearch"
              value={form.estimateNumber}
              onChange={(e) => setField("estimateNumber", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Valid Until</div>
            <input
              className="aeSearch"
              type="date"
              value={form.validUntil}
              onChange={(e) => setField("validUntil", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Currency</div>
            <input
              className="aeSearch"
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
              placeholder="USD"
            />
          </label>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="aeSmall">Tags (one per line)</div>
          <textarea
            className="aeSearch"
            style={{ minHeight: 80 }}
            value={form.tagsText}
            onChange={(e) => setField("tagsText", e.target.value)}
          />
        </div>
      </div>

      {/* CUSTOMER */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Customer</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Customer Type</div>
            <select
              className="aeSearch"
              value={form.customer.type}
              onChange={(e) => setField("customer.type", e.target.value)}
            >
              <option value="company">Company</option>
              <option value="individual">Individual</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Company Name</div>
            <input
              className="aeSearch"
              value={form.customer.companyName}
              onChange={(e) => setField("customer.companyName", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Primary Contact Name</div>
            <input
              className="aeSearch"
              value={form.customer.name}
              onChange={(e) => setField("customer.name", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Phone</div>
            <input
              className="aeSearch"
              value={form.customer.phone}
              onChange={(e) => setField("customer.phone", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Email</div>
            <input
              className="aeSearch"
              value={form.customer.email}
              onChange={(e) => setField("customer.email", e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Billing Address Line 1</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.line1}
              onChange={(e) => setField("customer.billingAddress.line1", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">City</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.city}
              onChange={(e) => setField("customer.billingAddress.city", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">State</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.state}
              onChange={(e) => setField("customer.billingAddress.state", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">ZIP</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.postal}
              onChange={(e) => setField("customer.billingAddress.postal", e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* SITE */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Site</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Property Name</div>
            <input
              className="aeSearch"
              value={form.site.propertyName}
              onChange={(e) => setField("site.propertyName", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Site Address Line 1</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.line1}
              onChange={(e) => setField("site.siteAddress.line1", e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">City</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.city}
              onChange={(e) => setField("site.siteAddress.city", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">State</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.state}
              onChange={(e) => setField("site.siteAddress.state", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">ZIP</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.postal}
              onChange={(e) => setField("site.siteAddress.postal", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Approx. Area</div>
            <input
              className="aeSearch"
              value={form.site.approxArea}
              onChange={(e) => setField("site.approxArea", e.target.value)}
              placeholder="98,000 sqft"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Roof Systems (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 70 }}
              value={form.site.roofSystemsText}
              onChange={(e) => setField("site.roofSystemsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Access</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 70 }}
              value={form.site.access}
              onChange={(e) => setField("site.access", e.target.value)}
              placeholder="Ladder access; escort requiredƒ?İ"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Site Constraints (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 90 }}
              value={form.site.siteConstraintsText}
              onChange={(e) => setField("site.siteConstraintsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Safety Requirements (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 90 }}
              value={form.site.safetyRequirementsText}
              onChange={(e) => setField("site.safetyRequirementsText", e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* PRICING */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Pricing</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Tax Mode</div>
            <select
              className="aeSearch"
              value={form.pricing.taxMode}
              onChange={(e) => setField("pricing.taxMode", e.target.value)}
            >
              <option value="exclusive">Exclusive (adds tax line)</option>
              <option value="inclusive">Inclusive (tax included)</option>
              <option value="none">No tax</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Tax Rate</div>
            <input
              className="aeSearch"
              value={form.pricing.taxRate}
              onChange={(e) => setField("pricing.taxRate", e.target.value)}
              placeholder="0.0825"
            />
          </label>

          <label>
            <div className="aeSmall">Fee ƒ?" Mobilization</div>
            <input
              className="aeSearch"
              value={form.pricing.fees?.[0]?.amount ?? 0}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.fees[0].amount = safeNum(v);
                  return next;
                });
              }}
              placeholder="0"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Discount Label</div>
            <input
              className="aeSearch"
              value={form.pricing.discounts?.[0]?.label ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.discounts[0].label = v;
                  return next;
                });
              }}
            />
          </label>

          <label>
            <div className="aeSmall">Discount Type</div>
            <select
              className="aeSearch"
              value={form.pricing.discounts?.[0]?.type ?? "percent"}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.discounts[0].type = v;
                  return next;
                });
              }}
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Discount Value</div>
            <input
              className="aeSearch"
              value={form.pricing.discounts?.[0]?.value ?? 0}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.discounts[0].value = safeNum(v);
                  return next;
                });
              }}
              placeholder="0.03 for 3%"
            />
          </label>
        </div>
      </div>

      {/* OPTIONS + SCOPE (full editor) */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <h3 style={{ margin: 0 }}>Options & Scope</h3>
          <button className="btn btnGhost" type="button" onClick={addOption}>
            + Add option
          </button>
        </div>

        <div style={{ marginTop: 10, display: "grid", gap: 14 }}>
          {form.options.map((opt, optionIdx) => {
            const computed = optionsWithTotals[optionIdx]?.totals || {
              subtotal: 0, taxTotal: 0, grandTotal: 0
            };

            return (
              <div key={opt.optionId} style={{ border: "1px solid rgba(15,23,42,.10)", borderRadius: 14, padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div className="pill info">{opt.optionId}</div>
                    <strong>{opt.title}</strong>
                  </div>

                  <button
                    className="btn btnTiny btnGhost"
                    type="button"
                    onClick={() => removeOption(optionIdx)}
                    disabled={form.options.length <= 1}
                  >
                    Remove option
                  </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginTop: 10 }}>
                  <label>
                    <div className="aeSmall">Title</div>
                    <input
                      className="aeSearch"
                      value={opt.title}
                      onChange={(e) => updateOption(optionIdx, { title: e.target.value })}
                    />
                  </label>

                  <div style={{ display: "grid", gap: 6 }}>
                    <div className="aeSmall">Totals (computed)</div>
                    <div className="aeSmall">
                      Subtotal: <strong>{Math.round(computed.subtotal).toLocaleString()}</strong>{" "}
                      | Tax: <strong>{Math.round(computed.taxTotal).toLocaleString()}</strong>{" "}
                      | Grand: <strong>{Math.round(computed.grandTotal).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>

                <label style={{ display: "block", marginTop: 10 }}>
                  <div className="aeSmall">Summary</div>
                  <textarea
                    className="aeSearch"
                    style={{ minHeight: 70 }}
                    value={opt.summary}
                    onChange={(e) => updateOption(optionIdx, { summary: e.target.value })}
                  />
                </label>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
                  <label>
                    <div className="aeSmall">Included (one per line)</div>
                    <textarea
                      className="aeSearch"
                      style={{ minHeight: 90 }}
                      value={opt.includedText}
                      onChange={(e) => updateOption(optionIdx, { includedText: e.target.value })}
                    />
                  </label>
                  <label>
                    <div className="aeSmall">Excluded (one per line)</div>
                    <textarea
                      className="aeSearch"
                      style={{ minHeight: 90 }}
                      value={opt.excludedText}
                      onChange={(e) => updateOption(optionIdx, { excludedText: e.target.value })}
                    />
                  </label>
                </div>

                <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong>Groups / Areas</strong>
                  <button className="btn btnTiny btnGhost" type="button" onClick={() => addGroup(optionIdx)}>
                    + Add group
                  </button>
                </div>

                <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
                  {opt.areas.map((g, groupIdx) => (
                    <div key={g.groupId} style={{ background: "rgba(15,23,42,.03)", borderRadius: 12, padding: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <div className="aeSmall">
                          <strong>{g.groupId}</strong>
                        </div>
                        <button
                          className="btn btnTiny btnGhost"
                          type="button"
                          onClick={() => removeGroup(optionIdx, groupIdx)}
                          disabled={opt.areas.length <= 1}
                        >
                          Remove group
                        </button>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr", gap: 12, marginTop: 8 }}>
                        <label>
                          <div className="aeSmall">Group Title</div>
                          <input
                            className="aeSearch"
                            value={g.title}
                            onChange={(e) => updateGroup(optionIdx, groupIdx, { title: e.target.value })}
                            placeholder="Area A — West Slope"
                          />
                        </label>
                        <label>
                          <div className="aeSmall">Notes</div>
                          <input
                            className="aeSearch"
                            value={g.notes}
                            onChange={(e) => updateGroup(optionIdx, groupIdx, { notes: e.target.value })}
                            placeholder="Work concentrated near ridge and transitions…"
                          />
                        </label>
                      </div>

                      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <strong>Line Items</strong>
                        <button className="btn btnTiny btnGhost" type="button" onClick={() => addItem(optionIdx, groupIdx)}>
                          + Add item
                        </button>
                      </div>

                      <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                        {g.items.map((it, itemIdx) => (
                          <div key={it.itemId} style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(15,23,42,.08)", padding: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                              <div className="aeSmall">
                                <strong>{it.itemId}</strong>
                              </div>
                              <button
                                className="btn btnTiny"
                                type="button"
                                onClick={() => removeItem(optionIdx, groupIdx, itemIdx)}
                                disabled={g.items.length <= 1}
                              >
                                Remove
                              </button>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                              <label>
                                <div className="aeSmall">Name</div>
                                <input
                                  className="aeSearch"
                                  value={it.name}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { name: e.target.value })}
                                />
                              </label>

                              <label>
                                <div className="aeSmall">Category</div>
                                <select
                                  className="aeSearch"
                                  value={it.category}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { category: e.target.value })}
                                >
                                  <option>Repairs</option>
                                  <option>Maintenance</option>
                                  <option>Coating</option>
                                  <option>Replacement</option>
                                  <option>Mobilization</option>
                                  <option>Safety</option>
                                  <option>Equipment</option>
                                  <option>Misc</option>
                                </select>
                              </label>
                            </div>

                            <label style={{ display: "block", marginTop: 8 }}>
                              <div className="aeSmall">Description</div>
                              <textarea
                                className="aeSearch"
                                style={{ minHeight: 70 }}
                                value={it.description}
                                onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { description: e.target.value })}
                              />
                            </label>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
                              <label>
                                <div className="aeSmall">Qty</div>
                                <input
                                  className="aeSearch"
                                  value={it.qty}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { qty: e.target.value })}
                                />
                              </label>
                              <label>
                                <div className="aeSmall">Unit</div>
                                <select
                                  className="aeSearch"
                                  value={it.unit}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { unit: e.target.value })}
                                >
                                  <option value="ea">ea</option>
                                  <option value="lf">lf</option>
                                  <option value="sqft">sqft</option>
                                  <option value="sq">sq</option>
                                  <option value="hr">hr</option>
                                  <option value="day">day</option>
                                  <option value="lot">lot</option>
                                </select>
                              </label>
                              <label>
                                <div className="aeSmall">Unit Price</div>
                                <input
                                  className="aeSearch"
                                  value={it.unitPrice}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { unitPrice: e.target.value })}
                                />
                              </label>
                              <label>
                                <div className="aeSmall">Taxable</div>
                                <select
                                  className="aeSearch"
                                  value={it.taxable ? "yes" : "no"}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { taxable: e.target.value === "yes" })}
                                >
                                  <option value="yes">Yes</option>
                                  <option value="no">No</option>
                                </select>
                              </label>
                              <div>
                                <div className="aeSmall">Line Total</div>
                                <div style={{ padding: "10px 12px" }}>
                                  <strong>
                                    {Math.round(safeNum(it.qty) * safeNum(it.unitPrice)).toLocaleString()}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                    </div>
                  ))}
                </div>

              </div>
            );
          })}
        </div>
      </div>

      {/* SCHEDULE / PAYMENT / LEGAL */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Schedule & Payment</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Lead Time</div>
            <input
              className="aeSearch"
              value={form.schedule.leadTime}
              onChange={(e) => setField("schedule.leadTime", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Duration</div>
            <input
              className="aeSearch"
              value={form.schedule.duration}
              onChange={(e) => setField("schedule.duration", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Work Window</div>
            <input
              className="aeSearch"
              value={form.schedule.workWindow}
              onChange={(e) => setField("schedule.workWindow", e.target.value)}
            />
          </label>
        </div>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="aeSmall">Milestones (one per line, ƒ?oLabel ƒ?" Notesƒ??)</div>
          <textarea
            className="aeSearch"
            style={{ minHeight: 90 }}
            value={form.schedule.milestonesText}
            onChange={(e) => setField("schedule.milestonesText", e.target.value)}
          />
        </label>

        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Deposit Required</div>
            <select
              className="aeSearch"
              value={form.payment.depositRequired ? "yes" : "no"}
              onChange={(e) => setField("payment.depositRequired", e.target.value === "yes")}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Deposit Type</div>
            <select
              className="aeSearch"
              value={form.payment.depositType}
              onChange={(e) => setField("payment.depositType", e.target.value)}
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Deposit Value</div>
            <input
              className="aeSearch"
              value={form.payment.depositValue}
              onChange={(e) => setField("payment.depositValue", e.target.value)}
              placeholder="0.3 or 5000"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Payment Methods (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 80 }}
              value={form.payment.paymentMethodsText}
              onChange={(e) => setField("payment.paymentMethodsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Invoicing Notes</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 80 }}
              value={form.payment.invoicing}
              onChange={(e) => setField("payment.invoicing", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Warranty & Legal</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Warranty Type</div>
            <input
              className="aeSearch"
              value={form.warranty.type}
              onChange={(e) => setField("warranty.type", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Warranty Term</div>
            <input
              className="aeSearch"
              value={form.warranty.term}
              onChange={(e) => setField("warranty.term", e.target.value)}
            />
          </label>
        </div>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="aeSmall">Warranty Details (one per line)</div>
          <textarea
            className="aeSearch"
            style={{ minHeight: 90 }}
            value={form.warranty.detailsText}
            onChange={(e) => setField("warranty.detailsText", e.target.value)}
          />
        </label>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Assumptions (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.assumptionsText}
              onChange={(e) => setField("assumptionsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Exclusions (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.exclusionsText}
              onChange={(e) => setField("exclusionsText", e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Clarifications (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.clarificationsText}
              onChange={(e) => setField("clarificationsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Terms (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.termsText}
              onChange={(e) => setField("termsText", e.target.value)}
            />
          </label>
        </div>
      </div>
    </div>
  );
}




